import { defineStore } from 'pinia';
import { ref } from 'vue';

import { api } from '~/lib/api';
import { onRealtime } from '~/lib/realtime';
import type { NotificationRow } from '~/stores/notifications';

/**
 * Tracks how many unanswered (status=pending) customer questions the active
 * store has — the count badge shown next to Sorular in the sidebar and the
 * bottom tab bar.
 *
 * The count is store-scoped: `refresh()` reads whatever store id the api layer
 * currently sends as X-Store-Id, so callers must re-fetch after switching
 * stores. Realtime `question.new` events bump it live; answering a question
 * (which has no realtime event) should call `refresh()` explicitly.
 */
export const useQuestionBadgeStore = defineStore('question-badge', () => {
  const pendingCount = ref(0);
  let unsubscribe: (() => void) | null = null;
  // Collapse bursts of realtime events into a single re-fetch: a bulk sync can
  // emit many `question.new` in quick succession, and one refetch each would
  // flood the API and trip the rate limiter.
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;
  const REFRESH_DEBOUNCE_MS = 600;

  function scheduleRefresh(): void {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      void refresh();
    }, REFRESH_DEBOUNCE_MS);
  }

  /** Re-read the pending-question count for the active store. Never throws. */
  async function refresh(): Promise<void> {
    try {
      // pageSize=1 — we only need `total`, not the rows themselves.
      const res = await api.get<{ total: number }>('/questions', {
        params: { status: 'pending', page: 1, pageSize: 1 },
      });
      pendingCount.value = res.data.total;
    } catch {
      // No active store / transient failure — drop the badge rather than show
      // a stale number.
      pendingCount.value = 0;
    }
  }

  /**
   * Subscribe to the realtime stream so an incoming question bumps the badge
   * without a navigation. Idempotent — safe to call on every login.
   */
  function attach(): void {
    if (unsubscribe) return;
    unsubscribe = onRealtime((e) => {
      if (e.type !== 'notification:new') return;
      const row = e.payload as NotificationRow;
      // Re-fetch instead of a naive +1 so the count stays correct for the
      // active store even when the notification belongs to a different one.
      if (row.type === 'question.new') scheduleRefresh();
    });
  }

  function reset(): void {
    pendingCount.value = 0;
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
  }

  return { pendingCount, refresh, attach, reset };
});
